import { GerenciadorPrecos } from '../../../../../@types/GerenciadorPrecos';
import { IPAMaths as calc } from '../../../../../lib';
import { isOutsidePercentageLimit } from '../limitInfoUtils';

type RowData = GerenciadorPrecos.RowData;
type OfferDetails = NonNullable<RowData['offerDetails']>;
type InputSkin = 'gray' | 'blue' | 'red';

const hasValue = (value: number | null | undefined): value is number => value !== null && value !== undefined;

const normalizePercentageLimit = (value: number): number => value * 100;

export const calcOfferCpiSkin = (offer: OfferDetails | null | undefined): InputSkin => {
  if (!offer) return 'gray';

  const { competitorsPrice, newCompetitivenessPrice, maxCpi, minCpi } = offer;

  if (!hasValue(competitorsPrice) || competitorsPrice <= 0 || !hasValue(newCompetitivenessPrice) || !hasValue(maxCpi) || !hasValue(minCpi)) {
    return 'gray';
  }

  if (newCompetitivenessPrice > normalizePercentageLimit(maxCpi) || newCompetitivenessPrice < normalizePercentageLimit(minCpi)) {
    return 'red';
  }

  return 'gray';
};

export const calcOfferMarginSkin = (offer: OfferDetails | null | undefined, displayedMargin?: number | null): InputSkin => {
  if (!offer) return 'gray';

  const { newMargin, maxMargin, minMargin } = offer;
  const margin = hasValue(newMargin) ? newMargin : displayedMargin;

  if (!hasValue(margin)) {
    return 'gray';
  }

  return isOutsidePercentageLimit(margin, minMargin, maxMargin) ? 'red' : 'gray';
};

export const getOfferDiscountBasePrice = (rowData: RowData) => {
  return rowData.retailPrice ?? rowData.offerDetails?.retailPrice ?? 0;
};

export const calcOfferDiscountPercentage = (rowData: RowData, offerPrice: number | null) => {
  const discountBasePrice = getOfferDiscountBasePrice(rowData);

  if (!discountBasePrice || offerPrice === null) {
    return 0;
  }

  return Math.max(0, Math.min(100, calc.calcDiscountPercentage(discountBasePrice, offerPrice) ?? 0));
};

export const handleCalcOfferPrice = (price: number | null, rowData: RowData) => {
  const { competitorsPrice, salesTaxPercentage, expensesPercent, cost } = rowData;

  const newCompetitivenessPrice = calc.novaCompetitividade(price, competitorsPrice);

  const newMargin = calc.novaMargem(price, salesTaxPercentage, expensesPercent, cost);

  return {
    price,
    newCompetitivenessPrice,
    newMargin,
  };
};

export const calcOfferRetailCPI = (retailPrice: number | null, competitorsPrice: number | null) => {
  if (retailPrice === null || retailPrice === undefined || competitorsPrice === null || competitorsPrice === undefined || competitorsPrice === 0) {
    return 0;
  }

  return calc.novaCompetitividade(retailPrice, competitorsPrice) ?? 0;
};

export const handleCalcOfferCPI = (newCompetitivenessPrice: number | null, rowData: RowData) => {
  const { competitorsPrice, salesTaxPercentage, expensesPercent, cost } = rowData;

  const price = calc.precoWithCpi(newCompetitivenessPrice, competitorsPrice);

  const newMargin = calc.novaMargem(price, salesTaxPercentage, expensesPercent, cost);

  return {
    price,
    newCompetitivenessPrice,
    newMargin,
  };
};
export const handleCalcOfferMargin = (newMargin: number | null, rowData: RowData) => {
  const { cost, expensesPercent, salesTaxPercentage, competitorsPrice } = rowData;

  const price = calc.precoWithMargem(newMargin, cost, expensesPercent, salesTaxPercentage);

  const newCompetitivenessPrice = calc.cpiWithMargem(newMargin, price, competitorsPrice);

  return {
    price,
    newCompetitivenessPrice,
    newMargin,
  };
};
